import React, { useRef, useState } from 'react';

import { parseCsv } from './utils';
import { Button, FormRadioField, ModalShell } from '../../components';

import './styles.scoped.css';

/**
 * CP-857: bulk import for a code table.
 *
 * Two modes, because the two real uses differ:
 *   Merge   — update the rows in the file, leave everything else alone (routine edits)
 *   Replace — the file becomes the whole list (a full re-sync from a spreadsheet)
 *
 * Nothing is written unless every row passes validation, so a rejected import leaves the
 * table exactly as it was. Rejections come back per row with their row number.
 */
const MODE_OPTIONS = [
  { label: 'Merge — update the rows in the file, keep the rest', value: 'MERGE' },
  { label: 'Replace — the file becomes the entire list', value: 'REPLACE' },
];

const ImportModal = ({ config, onImport, onCancel }) => {
  const [mode, setMode] = useState('MERGE');
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [parseErrors, setParseErrors] = useState([]);
  const [rowErrors, setRowErrors] = useState([]);
  const [formError, setFormError] = useState('');
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setParseErrors([]);
    setRowErrors([]);
    setFormError('');
  };

  const handleFile = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    reset();
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ''));
    reader.onerror = () => setFormError('That file could not be read.');
    reader.readAsText(file);
  };

  const handleImport = () => {
    reset();

    const { rows, errors } = parseCsv(config, text);
    if (errors.length && !rows.length) {
      setParseErrors(errors);
      return;
    }
    if (!rows.length) {
      setFormError('No rows found. Paste the list or choose a CSV file.');
      return;
    }
    // non-fatal notes (ignored extra columns, absent columns) are still worth showing
    setParseErrors(errors);

    setImporting(true);
    // As in CodeTableForm: on success the parent closes this dialog, so clearing the
    // in-flight flag belongs in catch rather than finally.
    onImport({ mode, rows }).catch(e => {
      setFormError(e.message);
      setRowErrors(e.rows ?? []);
      setImporting(false);
    });
  };

  const buttons = [
    { text: importing ? 'Importing…' : 'Import', onPress: handleImport, disabled: importing },
    { text: 'Cancel', variant: 'outline', onPress: onCancel, disabled: importing },
  ];

  return (
    <ModalShell
      title={`Import ${config.title}`}
      subtitle="Paste the list or choose a CSV file. Nothing is saved unless every row is valid."
      buttons={buttons}>
      <FormRadioField
        label="Import mode"
        name="code-table-import-mode"
        options={MODE_OPTIONS}
        value={mode}
        onChange={event => {
          reset();
          setMode(event?.target?.value ?? event);
        }}
        disabled={importing}
      />

      {mode === 'REPLACE' && (
        <p className="form-warning">
          Replace clears the current list first. Every removed row is recorded in the change history, where it can be
          read back if the import turns out to be wrong. {config.deleteWarning}
        </p>
      )}

      <div className="import-file-row">
        <Button
          variant="outline"
          text={fileName || 'Choose CSV file'}
          onPress={() => fileInputRef.current?.click()}
          disabled={importing}
        />
        {/* A file input cannot be styled, so it stays hidden and the Button drives it. */}
        <input
          ref={fileInputRef}
          className="import-file-input"
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
        />
      </div>

      {/* Control nested in its label rather than linked by htmlFor -- same a11y result, and
          it is the form jsx-a11y/label-has-associated-control recognizes. */}
      <label className="import-label" htmlFor="code-table-import-text">
        CSV contents
        <textarea
          id="code-table-import-text"
          className="import-textarea"
          value={text}
          disabled={importing}
          spellCheck={false}
          placeholder={config.fields.map(field => field.label).join(',')}
          onChange={event => {
            reset();
            setFileName('');
            setText(event.target.value);
          }}
        />
      </label>

      {!!parseErrors.length && (
        <ul className="import-notes">
          {parseErrors.map(message => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      {!!formError && <p className="form-error">{formError}</p>}

      {!!rowErrors.length && (
        <div className="import-errors">
          <table className="import-errors-table">
            <thead>
              <tr>
                <th>Row</th>
                <th>{config.keyLabel}</th>
                <th>Problem</th>
              </tr>
            </thead>
            <tbody>
              {rowErrors.map(error => (
                <tr key={`${error.RowNumber}-${error.Message}`}>
                  <td>{error.RowNumber}</td>
                  <td>{error.KeyValue}</td>
                  <td>{error.Message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  );
};

export default ImportModal;
